import { DEFAULT_RUN_MODE, type RunMode } from "./run/intent.ts";

/**
 * The viewer's own settings, kept in this browser's `localStorage` so they outlive the tab and belong to
 * whoever sits at it rather than to the account. Storage can be missing or refuse access (a private
 * window, blocked site data), so every read and write is guarded and falls back to the default.
 */

/**
 * The world zoom setting (ADR-061, "the default world zoom fits the cap" as amended by "world zoom 2.5 ships
 * where it fits"): a step relative to the default zoom for the window, Farther (-1), Default (0) or Closer (1),
 * each one place through the zooms allowed there (`stepWorldZoom` in `odyssey-shared`). The stored text is the
 * step, so a step saved before 2.5 shipped still reads as the same step: Closer at 1920 by 1080 gives 3, one
 * place from the default 2.5, where it gave 4 from the default 3.
 */
export type ZoomStep = -1 | 0 | 1;

/**
 * The storage key the zoom step lives under.
 */
export const ZOOM_STEP_KEY = "odyssey.zoomStep";

/**
 * What other players' nameplates draw (P1.9-T24; ADR-065, "the family is one look away"): the flag mark and the
 * first name, the flag mark and the full name, the flag mark alone, or no plate. The stored text is the value.
 */
export type OthersNameplates = "first" | "full" | "flag" | "off";

/**
 * Whether the player's own nameplate draws (P1.9-T24): off by default, since the vitals frame holds the name and
 * the flag. The stored text is the value.
 */
export type OwnNameplate = "off" | "on";

/**
 * The viewer's Nameplates choices, as the page and the nameplates hold them.
 */
export interface NameplateChoices {
  others: OthersNameplates;
  own: OwnNameplate;
}

/**
 * The Nameplates choices a viewer has before choosing: other players' first names, and no plate of their own.
 */
export const DEFAULT_NAMEPLATE_CHOICES: Readonly<NameplateChoices> = Object.freeze({ others: "first", own: "off" });

/**
 * The storage key other players' nameplates choice lives under.
 */
export const OTHERS_NAMEPLATES_KEY = "odyssey.nameplates.others";

/**
 * The storage key the player's own nameplate choice lives under.
 */
export const OWN_NAMEPLATE_KEY = "odyssey.nameplates.own";

/**
 * The part of the Web Storage interface the settings use.
 */
export interface SettingsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * This browser's `localStorage`, or null where reaching it throws or it does not exist.
 *
 * @returns The store, or null.
 */
export function localStore(): SettingsStore | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads one setting.
 *
 * @param key The storage key it lives under.
 * @param parse Reads the stored text, or null when nothing is stored, as the setting, its default for anything else.
 * @param store Where it is kept, or null when there is no store.
 * @returns The setting, or its default when nothing is stored, the store cannot be read or there is none.
 */
export function readSetting<T>(key: string, parse: (raw: string | null) => T, store: SettingsStore | null): T {
  if (store === null) {
    return parse(null);
  }
  try {
    return parse(store.getItem(key));
  } catch {
    return parse(null);
  }
}

/**
 * Keeps one setting.
 *
 * @param key The storage key it lives under.
 * @param value Its text.
 * @param store Where to keep it, or null when there is no store.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeSetting(key: string, value: string, store: SettingsStore | null): boolean {
  if (store === null) {
    return false;
  }
  try {
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads a stored value as a zoom step.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The step, or 0 for anything that is not exactly -1, 0 or 1.
 */
export function parseZoomStep(raw: string | null): ZoomStep {
  return raw === "-1" ? -1 : raw === "1" ? 1 : 0;
}

/**
 * Reads the viewer's zoom step.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The stored step, or 0 when none is stored, it is malformed or the store cannot be read.
 */
export function readZoomStep(store: SettingsStore | null = localStore()): ZoomStep {
  return readSetting(ZOOM_STEP_KEY, parseZoomStep, store);
}

/**
 * Keeps the viewer's zoom step.
 *
 * @param step The step to keep.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeZoomStep(step: ZoomStep, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(ZOOM_STEP_KEY, String(step), store);
}

/**
 * Reads a stored value as other players' nameplates choice.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The choice, or `first` for anything that is not exactly `first`, `full`, `flag` or `off`.
 */
export function parseOthersNameplates(raw: string | null): OthersNameplates {
  return raw === "full" || raw === "flag" || raw === "off" ? raw : "first";
}

/**
 * Reads a stored value as the player's own nameplate choice.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The choice, or `off` for anything that is not exactly `on`.
 */
export function parseOwnNameplate(raw: string | null): OwnNameplate {
  return raw === "on" ? "on" : "off";
}

/**
 * Reads the viewer's Nameplates choices.
 *
 * @param store Where they are kept; this browser's `localStorage` by default.
 * @returns A new object holding each stored choice, or its default when none is stored, it is not one of the
 *   choices or the store cannot be read.
 */
export function readNameplateChoices(store: SettingsStore | null = localStore()): NameplateChoices {
  return { others: readSetting(OTHERS_NAMEPLATES_KEY, parseOthersNameplates, store), own: readSetting(OWN_NAMEPLATE_KEY, parseOwnNameplate, store) };
}

/**
 * Keeps the viewer's choice for other players' nameplates.
 *
 * @param value The choice.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeOthersNameplates(value: OthersNameplates, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(OTHERS_NAMEPLATES_KEY, value, store);
}

/**
 * Keeps the viewer's choice for their own nameplate.
 *
 * @param value The choice.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeOwnNameplate(value: OwnNameplate, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(OWN_NAMEPLATE_KEY, value, store);
}

/**
 * The storage key the health numbers setting lives under (ADR-061's amendment "the player card").
 */
export const HEALTH_NUMBERS_KEY = "odyssey.healthNumbers";

/**
 * Reads a stored value as the health numbers setting: whether the player card's health bar shows its value. On by
 * default (the author's ruling of 2026-09-27).
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns False for exactly `off`, true for anything else.
 */
export function parseHealthNumbers(raw: string | null): boolean {
  return raw !== "off";
}

/**
 * Reads the viewer's health numbers setting.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns Whether the numbers show: true when nothing is stored, it is not `off` or the store cannot be read.
 */
export function readHealthNumbers(store: SettingsStore | null = localStore()): boolean {
  return readSetting(HEALTH_NUMBERS_KEY, parseHealthNumbers, store);
}

/**
 * Keeps the viewer's health numbers setting, as `on` or `off`.
 *
 * @param show Whether the numbers show.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeHealthNumbers(show: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(HEALTH_NUMBERS_KEY, show ? "on" : "off", store);
}

/**
 * The storage key the Show idle and AFK marks choice lives under (P1.9-T45, ADR-090 decision 9).
 */
export const IDLE_MARKS_KEY = "odyssey.idleMarks";

/**
 * Reads a stored value as the Show idle and AFK marks choice, on by default (P1.9-T45: the marks shown by default).
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns False for exactly `off`, true for anything else.
 */
export function parseIdleMarks(raw: string | null): boolean {
  return raw !== "off";
}

/**
 * Reads the viewer's Show idle and AFK marks choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns Whether the marks show: true when nothing is stored, it is not `off` or the store cannot be read.
 */
export function readIdleMarks(store: SettingsStore | null = localStore()): boolean {
  return readSetting(IDLE_MARKS_KEY, parseIdleMarks, store);
}

/**
 * Keeps the viewer's Show idle and AFK marks choice, as `on` or `off`.
 *
 * @param show Whether the marks show.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeIdleMarks(show: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(IDLE_MARKS_KEY, show ? "on" : "off", store);
}

/**
 * The storage key the Show other players' emotes choice lives under (P2.19-T7, ADR-114 decision 5.8).
 */
export const EMOTES_OTHERS_KEY = "odyssey.emotes.others";

/**
 * Reads a stored value as the Show other players' emotes choice, on by default (ADR-114 decision 5.8).
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns False for exactly `off`, true for anything else.
 */
export function parseEmotesOthers(raw: string | null): boolean {
  return raw !== "off";
}

/**
 * Reads the viewer's Show other players' emotes choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns Whether other players' emoji are drawn: true when nothing is stored, it is not `off` or the store cannot be
 *   read.
 */
export function readEmotesOthers(store: SettingsStore | null = localStore()): boolean {
  return readSetting(EMOTES_OTHERS_KEY, parseEmotesOthers, store);
}

/**
 * Keeps the viewer's Show other players' emotes choice, as `on` or `off`.
 *
 * @param show Whether other players' emoji are drawn.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeEmotesOthers(show: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(EMOTES_OTHERS_KEY, show ? "on" : "off", store);
}

/**
 * The storage key the Show key numbers on the bar choice lives under (P2.18-T15, ADR-111's note of 2026-10-08 for
 * P2.18-T15).
 */
export const BAR_KEYS_KEY = "odyssey.barKeys";

/**
 * Reads a stored value as the Show key numbers on the bar choice, off by default (the author's answer of 2026-10-08:
 * "Off, with a setting").
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns True for exactly `on`, false for anything else.
 */
export function parseBarKeys(raw: string | null): boolean {
  return raw === "on";
}

/**
 * Reads the viewer's Show key numbers on the bar choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns Whether the bar's slots draw their keys: false when nothing is stored, it is not `on` or the store cannot
 *   be read.
 */
export function readBarKeys(store: SettingsStore | null = localStore()): boolean {
  return readSetting(BAR_KEYS_KEY, parseBarKeys, store);
}

/**
 * Keeps the viewer's Show key numbers on the bar choice, as `on` or `off`.
 *
 * @param show Whether the bar's slots draw their keys.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeBarKeys(show: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(BAR_KEYS_KEY, show ? "on" : "off", store);
}

/**
 * The storage key the Lock the ability bar choice lives under (ADR-121 decision 3.2, P2.8-T29): per device, as the keys
 * are.
 */
export const BAR_LOCKED_KEY = "odyssey.bar.locked";

/**
 * Reads a stored value as the Lock the ability bar choice, off by default (ADR-121 decision 3.2).
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns True for exactly `on`, false for anything else.
 */
export function parseBarLocked(raw: string | null): boolean {
  return raw === "on";
}

/**
 * Reads the viewer's Lock the ability bar choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns Whether a drag from the bar needs Shift: false when nothing is stored, it is not `on` or the store cannot be
 *   read.
 */
export function readBarLocked(store: SettingsStore | null = localStore()): boolean {
  return readSetting(BAR_LOCKED_KEY, parseBarLocked, store);
}

/**
 * Keeps the viewer's Lock the ability bar choice, as `on` or `off`.
 *
 * @param locked Whether the bar is locked.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeBarLocked(locked: boolean, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(BAR_LOCKED_KEY, locked ? "on" : "off", store);
}

/**
 * The viewer's Ability bar choice (P1.9-T133, the author's request quoted in ADR-061's note for P1.9-T133): All 10
 * shows ten slots wherever they fit beside the chat slot and pages five at a time where they do not; Five at a time
 * pages the bar on any window. The stored text is the value.
 */
export type BarPagingChoice = "all" | "five";

/**
 * The storage key the Ability bar choice lives under.
 */
export const BAR_PAGING_KEY = "odyssey.barPaging";

/**
 * Reads a stored value as the Ability bar choice, All 10 by default.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns `five` for exactly `five`, `all` for anything else.
 */
export function parseBarPaging(raw: string | null): BarPagingChoice {
  return raw === "five" ? "five" : "all";
}

/**
 * Reads the viewer's Ability bar choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The choice: `all` when nothing is stored, it is not `five` or the store cannot be read.
 */
export function readBarPaging(store: SettingsStore | null = localStore()): BarPagingChoice {
  return readSetting(BAR_PAGING_KEY, parseBarPaging, store);
}

/**
 * Keeps the viewer's Ability bar choice.
 *
 * @param choice The choice.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeBarPaging(choice: BarPagingChoice, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(BAR_PAGING_KEY, choice, store);
}


/**
 * The storage key the Run setting lives under (ADR-116 decision 11): `hold` or `toggle`, kept per device.
 */
export const RUN_MODE_KEY = "odyssey.run.mode";

/**
 * Reads a stored value as the Run setting.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The mode, or Hold for anything that is not exactly `toggle`.
 */
export function parseRunMode(raw: string | null): RunMode {
  return raw === "toggle" ? "toggle" : DEFAULT_RUN_MODE;
}

/**
 * Reads the device's Run setting.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The stored mode, or Hold when none is stored, it is not one of the two or the store cannot be read.
 */
export function readRunMode(store: SettingsStore | null = localStore()): RunMode {
  return readSetting(RUN_MODE_KEY, parseRunMode, store);
}

/**
 * Keeps the device's Run setting.
 *
 * @param mode Hold or Toggle.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeRunMode(mode: RunMode, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(RUN_MODE_KEY, mode, store);
}
